import { create } from "zustand"
import { Email } from "../types"
import { EmailCompose } from "@pages/Email/Email.types"

interface EmailStore {
  emails: Email[]
  selectedEmails: string[]
  currentFolder: "inbox" | "sent" | "drafts" | "trash" | "spam"
  searchQuery: string
  filterLabels: string[]
  composeEmail: EmailCompose | null
  isComposing: boolean

  // Actions
  setEmails: (emails: Email[]) => void
  addEmail: (email: Email) => void
  updateEmail: (id: string, updates: Partial<Email>) => void
  deleteEmail: (id: string) => void
  deleteEmails: (ids: string[]) => void
  markAsRead: (id: string) => void
  markAsUnread: (id: string) => void
  markAsImportant: (id: string) => void
  markAsNotImportant: (id: string) => void
  toggleStar: (id: string) => void
  moveToFolder: (id: string, folder: Email["folder"]) => void
  moveEmailsToFolder: (ids: string[], folder: Email["folder"]) => void

  // Selection
  selectEmail: (id: string) => void
  deselectEmail: (id: string) => void
  selectAllEmails: () => void
  clearSelection: () => void

  // Filters
  setCurrentFolder: (folder: Email["folder"]) => void
  setSearchQuery: (query: string) => void
  setFilterLabels: (labels: string[]) => void
  clearFilters: () => void

  // Compose
  startComposing: (email?: EmailCompose) => void
  updateComposeEmail: (updates: Partial<EmailCompose>) => void
  sendEmail: (email: EmailCompose) => void
  cancelComposing: () => void

  // Getters
  getFilteredEmails: () => Email[]
  getUnreadCount: () => number
  getEmailsByFolder: (folder: Email["folder"]) => Email[]
}

export const useEmailStore = create<EmailStore>((set, get) => ({
  emails: [],
  selectedEmails: [],
  currentFolder: "inbox",
  searchQuery: "",
  filterLabels: [],
  composeEmail: null,
  isComposing: false,

  setEmails: (emails: Email[]) => {
    set({ emails })
  },

  addEmail: (email: Email) => {
    set((state) => ({
      emails: [email, ...state.emails],
    }))
  },

  updateEmail: (id: string, updates: Partial<Email>) => {
    set((state) => ({
      emails: state.emails.map((email) => (email.id === id ? { ...email, ...updates } : email)),
    }))
  },

  deleteEmail: (id: string) => {
    set((state) => ({
      emails: state.emails.filter((email) => email.id !== id),
      selectedEmails: state.selectedEmails.filter((emailId) => emailId !== id),
    }))
  },

  deleteEmails: (ids: string[]) => {
    set((state) => ({
      emails: state.emails.filter((email) => !ids.includes(email.id)),
      selectedEmails: state.selectedEmails.filter((emailId) => !ids.includes(emailId)),
    }))
  },

  markAsRead: (id: string) => {
    get().updateEmail(id, { isRead: true })
  },

  markAsUnread: (id: string) => {
    get().updateEmail(id, { isRead: false })
  },

  markAsImportant: (id: string) => {
    get().updateEmail(id, { isImportant: true })
  },

  markAsNotImportant: (id: string) => {
    get().updateEmail(id, { isImportant: false })
  },

  toggleStar: (id: string) => {
    const email = get().emails.find((e) => e.id === id)
    if (email) {
      get().updateEmail(id, { isStarred: !email.isStarred })
    }
  },

  moveToFolder: (id: string, folder: Email["folder"]) => {
    get().updateEmail(id, { folder })
  },

  moveEmailsToFolder: (ids: string[], folder: Email["folder"]) => {
    ids.forEach((id) => get().moveToFolder(id, folder))
    get().clearSelection()
  },

  selectEmail: (id: string) => {
    set((state) => ({
      selectedEmails: [...state.selectedEmails, id],
    }))
  },

  deselectEmail: (id: string) => {
    set((state) => ({
      selectedEmails: state.selectedEmails.filter((emailId) => emailId !== id),
    }))
  },

  selectAllEmails: () => {
    const filteredEmails = get().getFilteredEmails()
    set({ selectedEmails: filteredEmails.map((email) => email.id) })
  },

  clearSelection: () => {
    set({ selectedEmails: [] })
  },

  setCurrentFolder: (folder: Email["folder"]) => {
    set({ currentFolder: folder })
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
  },

  setFilterLabels: (labels: string[]) => {
    set({ filterLabels: labels })
  },

  clearFilters: () => {
    set({ searchQuery: "", filterLabels: [] })
  },

  startComposing: (email?: EmailCompose) => {
    set({
      isComposing: true,
      composeEmail: email || { to: "", subject: "", body: "" },
    })
  },

  updateComposeEmail: (updates: Partial<EmailCompose>) => {
    set((state) => ({
      composeEmail: state.composeEmail ? { ...state.composeEmail, ...updates } : null,
    }))
  },

  sendEmail: (email: EmailCompose) => {
    const newEmail: Email = {
      id: `email-${Date.now()}`,
      from: "user@example.com",
      fromName: "Usuario",
      to: email.to,
      subject: email.subject,
      body: email.body,
      isRead: true,
      isImportant: false,
      isStarred: false,
      hasAttachments: false,
      attachments: [],
      receivedAt: new Date().toISOString(),
      folder: "sent",
      labels: [],
      threadId: `thread-${Date.now()}`,
    }

    get().addEmail(newEmail)
    get().cancelComposing()
  },

  cancelComposing: () => {
    set({ isComposing: false, composeEmail: null })
  },

  getFilteredEmails: () => {
    const { emails, currentFolder, searchQuery, filterLabels } = get()

    return emails.filter((email) => {
      // Filter by folder
      if (email.folder !== currentFolder) return false

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesSearch =
          email.subject.toLowerCase().includes(query) ||
          email.fromName.toLowerCase().includes(query) ||
          email.body.toLowerCase().includes(query)
        if (!matchesSearch) return false
      }

      // Filter by labels
      if (filterLabels.length > 0) {
        const hasMatchingLabel = filterLabels.some((label) => email.labels.includes(label))
        if (!hasMatchingLabel) return false
      }

      return true
    })
  },

  getUnreadCount: () => {
    return get().emails.filter((email) => !email.isRead && email.folder === "inbox").length
  },

  getEmailsByFolder: (folder: Email["folder"]) => {
    return get().emails.filter((email) => email.folder === folder)
  },
}))
